import { useCallback, useEffect, useRef, useState } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useAuth from '../../hooks/useAuth';
import { getProducts } from '../../api/products';
import { PRODUCT_LIMIT } from '../../CONSTANTS';
import {
  resolveExistingProductEvidence,
  type ExistingProductEvidence,
  type ExistingProductFetchState,
} from './firstProductExistingData';
import type { FirstProductLocationGuardState } from './firstProductLocationGuard';

/**
 * Fetches existing real-location products for the wizard's first-product step
 * (US3, T057). The fetch runs only once the location guard is ready and is
 * scoped to the verified real location via the request interceptor's
 * `X-Location-ID`. Loading and errors are never treated as "zero products".
 */

const readTotalDocs = (response: any): number => {
  const data = response?.data?.data;
  const total = data?.totalDocs;
  return typeof total === 'number' && Number.isFinite(total) ? total : 0;
};

export const useFirstProductExistingData = (
  guard: FirstProductLocationGuardState,
): {
  fetchState: ExistingProductFetchState;
  evidence: ExistingProductEvidence;
  refetch: () => void;
} => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const [fetchState, setFetchState] = useState<ExistingProductFetchState>({
    status: 'idle',
  });
  const requestSeq = useRef(0);

  const readyLocationId = guard.kind === 'ready' ? guard.location.id : null;

  const runFetch = useCallback(
    (locationId: string) => {
      const seq = ++requestSeq.current;
      setFetchState({ status: 'loading' });
      getProducts(axiosPrivate, 1, PRODUCT_LIMIT, auth)
        .then(response => {
          if (seq !== requestSeq.current) return;
          if (response?.data?.status === 200) {
            setFetchState({
              status: 'success',
              totalDocs: readTotalDocs(response),
              locationId,
            });
          } else {
            setFetchState({ status: 'error' });
          }
        })
        .catch(() => {
          if (seq !== requestSeq.current) return;
          setFetchState({ status: 'error' });
        });
    },
    [auth, axiosPrivate],
  );

  useEffect(() => {
    if (!readyLocationId) {
      // Guard not ready: reset so stale success cannot satisfy a new location.
      requestSeq.current++;
      setFetchState({ status: 'idle' });
      return;
    }
    runFetch(readyLocationId);
  }, [readyLocationId, runFetch]);

  const refetch = useCallback(() => {
    if (readyLocationId) runFetch(readyLocationId);
  }, [readyLocationId, runFetch]);

  return {
    fetchState,
    evidence: resolveExistingProductEvidence(guard, fetchState),
    refetch,
  };
};

export default useFirstProductExistingData;
